import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ProfileOrder } from '@gg/shared';
import { Badge, buttonClasses, Card, PlusIcon } from '@/components/ui';
import { formatDate, formatDay, formatTime } from '@/lib/dates';
import { formatPrice } from '@/lib/i18n';
import { localName } from '@/lib/formats';
import { useMyOrders } from '@/lib/queries';

/**
 * "Mes courses" on the profile (screen 06, P-10): each race with its date and price, then per
 * order the amount paid, the order date, the insurance status (P-19), the insurer's page to
 * declare a cancellation while it can be made (decision 0025) and the Stripe receipt.
 * "Ajouter une course" starts the upsell (P-13).
 */
export function PurchasesCard() {
  const { t } = useTranslation();
  const { data, isError } = useMyOrders();

  return (
    <Card aria-labelledby="purchases-title">
      <h2 id="purchases-title" className="m-0 font-display text-section font-bold">
        {t('purchases.title')}
      </h2>
      {isError && <p className="m-0 text-sm text-danger">{t('error.generic')}</p>}
      {data?.orders.length === 0 && (
        <p className="m-0 text-sm text-ink-muted">{t('purchases.empty')}</p>
      )}
      {data?.orders.map((o) => (
        <OrderRows key={o.id} order={o} />
      ))}
      <Link to="/formats?add=1" className={buttonClasses('secondary', 'md')}>
        <PlusIcon size={16} />
        {t('purchases.add')}
      </Link>
    </Card>
  );
}

function OrderRows({ order: o }: { order: ProfileOrder }) {
  const { t } = useTranslation();
  const claimed = o.insurance?.status === 'claimed' || o.insurance?.status === 'indemnified';
  const badge =
    o.status === 'refunded' ? (
      <Badge>{t('purchases.refunded')}</Badge>
    ) : claimed ? (
      <Badge>{t('purchases.claimed')}</Badge>
    ) : o.insured ? (
      <Badge tone="success">{t('purchases.insured')}</Badge>
    ) : (
      <Badge>{t('purchases.nonRefundable')}</Badge>
    );

  return (
    <div className="flex flex-col gap-2 border-t border-line py-3">
      {o.registrations.map((r) => (
        <div key={r.id} className="flex flex-col gap-0.5">
          <span className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 font-bold">
              {localName(r.formatName, r.formatNameEn)}
              {r.team && (
                <Badge>{t(r.team.isCaptain ? 'teams.captainBadge' : 'teams.memberBadge')}</Badge>
              )}
            </span>
            {r.team && o.status !== 'refunded' && !claimed && (
              <Link
                to={`/me/teams/${r.team.id}`}
                className="shrink-0 text-helper font-semibold no-underline"
              >
                {t(r.team.isCaptain ? 'teams.manage' : 'teams.view')}
              </Link>
            )}
          </span>
          <span className="text-helper text-ink-muted">
            {[
              r.formatStartsAt &&
                `${formatDay(r.formatStartsAt, 'short')} ${formatTime(r.formatStartsAt)}`,
              formatPrice(r.priceCents),
              r.team && t('purchases.teamMember', { name: r.team.name }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2.5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-helper text-ink-muted">
          {badge}
          {t('purchases.paid', { amount: formatPrice(o.totalCents), date: formatDate(o.paidAt) })}
        </span>
        <span className="flex shrink-0 gap-3">
          {/* The insurer's page to declare a cancellation (decision 0025). */}
          {o.status === 'paid' && o.insurance?.claimUrl && (
            <a
              href={o.insurance.claimUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap text-helper font-semibold no-underline"
            >
              {t('purchases.claim')}
            </a>
          )}
          {o.receiptUrl && (
            <a
              href={o.receiptUrl}
              target="_blank"
              rel="noreferrer"
              className="whitespace-nowrap text-helper font-semibold no-underline"
            >
              {t('purchases.receipt')}
            </a>
          )}
        </span>
      </div>
    </div>
  );
}
